import { useState, FormEvent, useRef, useImperativeHandle, forwardRef, useEffect } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { Spinner } from '@/components/ui/Spinner';
import { useCreateLedgerReturn } from '@/api/hooks/useLedgerReturns';
import { useAuth } from '@/contexts/AuthContext';
import { useDefaultDate } from '@/hooks/useDefaultDate';
import { ExpandableForm } from '@/components/ExpandableForm';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { MoneyInput } from '@/components/ui/MoneyInput';
import { IconButton } from '@/components/ui/IconButton';
import { toast } from 'sonner';
import { ApiError } from '@/api/client';
import type { LedgerReturnType } from '@/api/types';
import { useCurrencyFractionDigits } from '@/hooks/useCurrencyFractionDigits';
import { decimalToMinorUnits } from '@/utils/money';

const TYPE_OPTIONS: { value: LedgerReturnType; label: string }[] = [
    { value: 'inflow', label: 'Inflow (money in)' },
    { value: 'outflow', label: 'Outflow (money out)' },
];

export interface AddReturnFormHandle {
    /** Collapse the form programmatically. */
    collapse: () => void;
}

interface AddReturnFormProps {
    /** The ledger ID to add return to. */
    ledgerId: number;
    /** The user's max value date across all ledgers (for smart defaults). */
    userMaxValueDate?: string;
    /** The currency used in the latest value (for smart defaults). */
    defaultCurrency?: string;
    /** Callback when form is opened (e.g., to close edit rows). */
    onFormOpen?: () => void;
    /** Callback when return is successfully created. */
    onSuccess?: () => void;
    /** Whether to start with the form expanded (default: false). */
    defaultExpanded?: boolean;
}

/**
 * Expandable form for adding a new return to a ledger.
 * Collapsed by default with "Add new return" label.
 * Expanded form contains: type selector, date picker, amount input, currency select.
 * Manages its own expanded state to prevent collapse on errors.
 */
export const AddReturnForm = forwardRef<AddReturnFormHandle, AddReturnFormProps>(function AddReturnForm({
    ledgerId,
    userMaxValueDate,
    defaultCurrency,
    onFormOpen,
    onSuccess,
    defaultExpanded = false,
}, ref) {
    const { settings } = useAuth();
    const defaultDate = useDefaultDate(userMaxValueDate);
    const createReturn = useCreateLedgerReturn(ledgerId);
    const formRef = useRef<HTMLFormElement>(null);

    // Internal expanded state - form manages its own visibility
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);

    // Expose collapse method to parent via ref
    useImperativeHandle(ref, () => ({
        collapse: () => setIsExpanded(false),
    }));

    const handleToggle = () => {
        const willExpand = !isExpanded;
        setIsExpanded(willExpand);
        if (willExpand) {
            onFormOpen?.();
        }
    };

    // Compute the default currency: ledger's last currency, then user's display currency, then USD
    const computeDefaultCurrency = defaultCurrency || settings?.displayCurrency || 'USD';

    // Form state
    const [type, setType] = useState<LedgerReturnType>('inflow');
    const [date, setDate] = useState(defaultDate);
    const [amount, setAmount] = useState('');
    const [currency, setCurrency] = useState(computeDefaultCurrency);
    const [label, setLabel] = useState('');
    const [errors, setErrors] = useState<Record<string, string>>({});
    const fractionDigits = useCurrencyFractionDigits(currency);

    // Sync date when default changes (handles async data loading)
    useEffect(() => {
        setDate(defaultDate);
    }, [defaultDate]);

    const resetForm = () => {
        setType('inflow');
        setDate(defaultDate);
        setAmount('');
        setCurrency(computeDefaultCurrency);
        setLabel('');
        setErrors({});
    };

    const handleSubmit = async (e?: FormEvent) => {
        e?.preventDefault();
        setErrors({});

        // Validation
        const newErrors: Record<string, string> = {};

        if (!date) {
            newErrors.date = 'Date is required';
        }

        if (!amount.trim()) {
            newErrors.amount = 'Amount must be greater than 0';
        }

        if (!currency) {
            newErrors.currency = 'Currency is required';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        let amountInMinor: number;
        try {
            amountInMinor = decimalToMinorUnits(amount, fractionDigits);
            if (amountInMinor <= 0) {
                throw new Error('Amount must be greater than 0');
            }
        } catch (error) {
            setErrors({ amount: error instanceof Error ? error.message : 'Invalid amount' });
            return;
        }

        try {
            // Convert date to ISO 8601 string (midnight UTC)
            const dateISO = new Date(date + 'T00:00:00Z').toISOString();

            await createReturn.mutateAsync({
                type,
                value: amountInMinor,
                currency,
                date: dateISO,
                label: label || null,
            });

            toast.success(`${type === 'inflow' ? 'Inflow' : 'Outflow'} added successfully`);
            resetForm();
            setIsExpanded(false);
            onSuccess?.();
        } catch (error) {
            const isTransient = error instanceof ApiError && error.isTransient;
            toast.error(`Failed to add cash flow.${isTransient ? ' Please try again.' : ''}`);
            console.error('Failed to create return:', error);
        }
    };

    const headerActions = (
        <IconButton
            variant="accent"
            size="md"
            onClick={(e) => {
                e.stopPropagation();
                handleSubmit();
            }}
            disabled={createReturn.isPending}
            aria-label="Add cash flow"
            title="Add Cash Flow"
        >
            {createReturn.isPending ? (
                <Spinner />
            ) : (
                <CheckIcon className="h-5 w-5" />
            )}
        </IconButton>
    );

    return (
        <ExpandableForm
            title="Add new cash flow"
            isExpanded={isExpanded}
            onToggle={handleToggle}
            headerActions={headerActions}
        >
            <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <Select
                        label="Type"
                        value={type}
                        onChange={(val) => setType(val as LedgerReturnType)}
                        options={TYPE_OPTIONS}
                    />
                    <Input
                        label="Date"
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        error={errors.date}
                        required
                    />
                    <MoneyInput
                        label="Amount"
                        value={amount}
                        currency={currency}
                        onValueChange={setAmount}
                        onCurrencyChange={setCurrency}
                        error={errors.amount || errors.currency}
                        placeholder="0.00"
                    />
                </div>

                <div>
                    <Input
                        label="Label (optional)"
                        type="text"
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        placeholder="e.g., Dividend, Withdrawal"
                    />
                </div>
                {/* Hidden submit button to enable Enter key submission */}
                <button type="submit" className="hidden" aria-hidden="true" />
            </form>
        </ExpandableForm>
    );
});
